import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { motion, AnimatePresence } from 'framer-motion';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

// Layouts
import DashboardLayout from './components/layouts/DashboardLayout';
import AuthLayout from './components/layouts/AuthLayout';
import PublicLayout from './components/layouts/PublicLayout';
import AnimatedLoader from './components/common/AnimatedLoader';

// Pages
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Purchases from './pages/Purchases';
import Expenses from './pages/Expenses';
import Invoices from './pages/Invoices';
import Quotations from './pages/Quotations';
import CreateInvoice from './pages/CreateInvoice';
import InvoiceCreation from './pages/InvoiceCreation';
import ModernInvoiceCreation from './pages/ModernInvoiceCreation';
import ModernQuotationCreation from './pages/ModernQuotationCreation';
import Inventory from './pages/Inventory';
import POS from './pages/POS';
import Payroll from './pages/Payroll';
import LeaveDashboard from './pages/LeaveDashboard';
import DataImport from './pages/DataImport';
import Reports from './pages/Reports';
import SalesInsights from './pages/SalesInsights';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Home from './pages/Home';
import Features from './pages/Features';
import Blogs from './pages/Blogs';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import CompleteProfile from './pages/auth/CompleteProfile';
import StaffOnboarding from './pages/auth/StaffOnboarding';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import ForgotPassword from './pages/auth/ForgotPassword';
import Clients from './pages/Clients';
import Vendors from './pages/Vendors';
import CashBook from './pages/CashBook';
import BankBook from './pages/BankBook';
import Transactions from './pages/Transactions';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import InvoiceDemo from './pages/InvoiceDemo';
import InvoiceTemplatePreview from './pages/InvoiceTemplatePreview';
import InvoiceThermalDemo from './pages/InvoiceThermalDemo';
import TestingTools from './pages/TestingTools';
import CustomTemplateBuilder from './pages/CustomTemplateBuilder';
import Receivables from './pages/Receivables';
import AccountsPayable from './pages/AccountsPayable';

// Admin Pages and Layout
import AdminLayout from './components/layouts/AdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import UserManagement from './pages/admin/UserManagement';
import ResourceMonitor from './pages/admin/ResourceMonitor';
import SecurityCenter from './pages/admin/SecurityCenter';
import UserDetails from './pages/admin/UserDetails';
import StaffManagement from './pages/admin/StaffManagement';

// Auth Provider
import { AuthProvider, useAuth } from './context/AuthContext';
import { WidgetSettingsProvider } from './context/WidgetSettingsContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SetupGuideProvider } from './context/SetupGuideContext';
import { SyncProvider } from './context/SyncContext';

// Import CSS
import './styles/globals.css';

import EmailVerificationPrompt from './components/EmailVerificationPrompt';

// Import offline mode components and services
import indexedDBService from './services/IndexedDBService';
import syncService from './services/SyncService';
import IndexedDBLoadingIndicator from './components/common/IndexedDBLoadingIndicator';

// Import custom hooks
import useSyncUserData from './hooks/useSyncUserData';
// Imports removed: checkMissingBusinessFields, MissingDetailsCheck
import { isAdminEmail } from './config/admin';

// Animation variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeInOut"
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3
    }
  }
};

// Animated Page Component
const AnimatedPage = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial="initial"
    animate="animate"
    exit="exit"
    variants={pageVariants}
    style={{ width: '100%' }}
  >
    {children}
  </motion.div>
);

const getModuleAccessForPath = (path: string, accessMap: any): 'none' | 'view' | 'edit' => {
  if (!accessMap) return 'none';
  if (path.startsWith('/dashboard')) return accessMap.dashboard || 'none';
  if (path.startsWith('/sales') || path.startsWith('/pos') || path.startsWith('/invoices') || path.startsWith('/quotations') || path.startsWith('/clients')) return accessMap.pos || 'none';
  if (path.startsWith('/purchases') || path.startsWith('/inventory') || path.startsWith('/vendors')) return accessMap.inventory || 'none';
  if (path.startsWith('/cashbook') || path.startsWith('/bankbook') || path.startsWith('/receivables') || path.startsWith('/payables') || path.startsWith('/transactions')) return accessMap.finance || 'none';
  if (path.startsWith('/expenses')) return accessMap.expenses || 'none';
  if (path.startsWith('/reports') || path.startsWith('/sales-insights')) return accessMap.reports || 'none';
  if (path.startsWith('/payroll')) return accessMap.payroll || 'none';
  if (path.startsWith('/leaves')) return accessMap.leaves || 'none';
  if (path.startsWith('/staff-management')) return accessMap.staff || 'none';
  if (path.startsWith('/settings') || path.startsWith('/import-data')) return accessMap.settings || 'none';
  return 'none';
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({ children, allowedRoles }) => {
  const { currentUser, userProfile, loading, logout } = useAuth();
  const location = useLocation();

  // Show loading while auth state is being determined
  if (loading) {
    return <AnimatedLoader message="Loading..." duration={500} />;
  }

  // If not authenticated, redirect to login immediately
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If the user's account has been revoked, force logout and redirect
  if (currentUser?.role === 'revoked' || userProfile?.role === 'revoked' || (!userProfile && currentUser && currentUser.role !== 'admin' && currentUser.role !== 'accountant' && currentUser.role !== 'cashier')) {
    // Only treat as revoked if we've finished loading and no profile exists
    // This catches the case where loadUserProfile returned null due to revocation
    if (currentUser?.role === 'revoked' || userProfile?.role === 'revoked') {
      logout().catch(console.error);
      return <Navigate to="/login" replace state={{ message: 'Your access has been revoked by the workspace admin.' }} />;
    }
  }

  // Check if profile is complete
  // Staff members (with tenantId) are always considered complete — they joined a workspace
  const isStaffMember = !!(currentUser?.tenantId) || !!(userProfile?.tenantId);
  const isProfileComplete = userProfile?.setupCompleted || isStaffMember;

  // Newly invited staff: has tenantId but has never set their display name
  // Redirect them to the staff onboarding page (name, phone, DOB)
  const needsStaffOnboarding = isStaffMember &&
    !userProfile?.staffOnboardingCompleted &&
    !userProfile?.displayName &&
    location.pathname !== '/staff-onboarding';

  if (needsStaffOnboarding) {
    return <Navigate to="/staff-onboarding" replace />;
  }

  // If profile is NOT complete and user is NOT on the completion page, redirect them
  if (!isProfileComplete && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  // If profile IS complete and user is on the completion page, redirect to dashboard
  if (isProfileComplete && location.pathname === '/complete-profile') {
    return <Navigate to="/dashboard" replace />;
  }

  // Check Granular RBAC
  const isSuperAdmin = isAdminEmail(currentUser.email || '');
  const isAdmin = currentUser.role === 'admin' || userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  
  if (!isSuperAdmin && !isAdmin) {
    const accessMap = userProfile?.hrDetails?.module_access;
    if (accessMap) {
      const access = getModuleAccessForPath(location.pathname, accessMap);
      if (access === 'none') {
        // Whitelist safe routes
        if (!['/dashboard', '/profile'].includes(location.pathname)) {
          return <Navigate to="/dashboard" replace />;
        }
      }
    } else if (allowedRoles && allowedRoles.length > 0) {
      // Legacy role check fallback
      const userRole = currentUser.role || userProfile?.role || 'user';
      if (!allowedRoles.includes(userRole)) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return <>{children}</>;
};

// Public Route Component (for auth pages)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile, loading: authLoading, isSigningUp } = useAuth();

  // Show loading while auth state is being determined
  if (authLoading) {
    return <AnimatedLoader fastLoad={true} duration={500} />;
  }

  // If authenticated and not in signup process, redirect based on profile status
  if (currentUser && !isSigningUp) {
    const isProfileComplete = userProfile?.setupCompleted || isAdminEmail(currentUser.email || '');
    if (isAdminEmail(currentUser.email)) {
      return <Navigate to="/admin" replace />;
    }
    if (isProfileComplete) {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/complete-profile" replace />;
    }
  }

  return <>{children}</>;
};

// Home page with forced light theme
const HomePageWithLightTheme: React.FC = () => {
  // Force light theme
  const { setThemeMode } = useTheme();

  useEffect(() => {
    // Remember user's original theme
    const originalTheme = localStorage.getItem('themeMode') || 'light';

    // Force light theme
    setThemeMode('light');

    // Restore original theme when leaving home page
    return () => {
      setThemeMode(originalTheme as 'light' | 'dark');
    };
  }, [setThemeMode]);

  return <Home />;
};


// Smart Redirect Component
const SmartRedirect: React.FC = () => {
  const { currentUser, userProfile, loading } = useAuth();

  // Show loading while auth state is being determined
  if (loading) {
    return <AnimatedLoader message="Loading..." duration={500} />;
  }

  // If not authenticated, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, check profile status
  const isProfileComplete = userProfile?.setupCompleted || isAdminEmail(currentUser.email || '');

  if (isAdminEmail(currentUser.email)) {
    return <Navigate to="/admin" replace />;
  }

  if (isProfileComplete) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/complete-profile" replace />;
  }
};

// ThemeSwitchingApp component that uses the ThemeContext
const ThemeSwitchingApp: React.FC = () => {
  const { theme } = useTheme();
  const { currentUser, showEmailVerification, setShowEmailVerification, handleEmailVerified } = useAuth();
  const [showDBIndicator, setShowDBIndicator] = useState(!indexedDBService.isInitialized());
  const [isCacheInitialized, setIsCacheInitialized] = useState(false);

  // Use the sync user data hook to sync user data to IndexedDB
  const syncStatus = useSyncUserData();

  // Initialize the IndexedDB and sync service
  useEffect(() => {
    const initializeOfflineServices = async () => {
      try {
        setShowDBIndicator(true);
        // Initialize DB with a longer timeout to ensure complete initialization
        await indexedDBService.initDB();

        // Pre-fetch and cache essential data
        if (currentUser) {
          // Start sync service
          // syncService.startService();

          // Force an immediate sync to ensure fresh data
          try {
            // await syncService.syncUserData();
          } catch (syncError) {
            console.warn('Initial sync encountered an issue:', syncError);
            // Continue anyway as this is non-fatal
          }
        }

        setIsCacheInitialized(true);
      } catch (error) {
        console.error('Error initializing IndexedDB:', error);
        setShowDBIndicator(false);
        // Mark as initialized even if there was an error to avoid blocking the UI
        setIsCacheInitialized(true);
      }
    };

    initializeOfflineServices();

    // Clean up sync service on unmount
    return () => {
      syncService.stopService();
    };
  }, [currentUser]);

  // Handle database initialization complete
  const handleDBInitialized = () => {
    setShowDBIndicator(false);
    // If user is logged in, trigger a sync
    if (currentUser && navigator.onLine) {
      // syncService.triggerSync(); // Disabled for local-only mode
    }
  };

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        maxWidth: '100vw',
        overflow: 'hidden'
      }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Router>
            <AnimatePresence initial={false}>
              <Routes>
                {/* Home and Public Pages */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={
                    <PublicRoute>
                      <HomePageWithLightTheme />
                    </PublicRoute>
                  } />
                  <Route path="/features" element={
                    <AnimatedPage>
                      <Features />
                    </AnimatedPage>
                  } />
                  <Route path="/blogs" element={
                    <AnimatedPage>
                      <Blogs />
                    </AnimatedPage>
                  } />
                  <Route path="/pricing" element={
                    <AnimatedPage>
                      <Pricing />
                    </AnimatedPage>
                  } />
                  <Route path="/contact" element={
                    <AnimatedPage>
                      <Contact />
                    </AnimatedPage>
                  } />
                  <Route path="/about" element={
                    <AnimatedPage>
                      <About />
                    </AnimatedPage>
                  } />
                  <Route path="/privacy-policy" element={
                    <AnimatedPage>
                      <PrivacyPolicy />
                    </AnimatedPage>
                  } />
                  <Route path="/terms-of-service" element={
                    <AnimatedPage>
                      <TermsOfService />
                    </AnimatedPage>
                  } />
                </Route>

                {/* Auth Routes */}
                <Route path="/login" element={
                  <PublicRoute>
                    <AuthLayout>
                      <AnimatedPage>
                        <Login />
                      </AnimatedPage>
                    </AuthLayout>
                  </PublicRoute>
                } />
                <Route path="/signup" element={
                  <PublicRoute>
                    <AuthLayout>
                      <AnimatedPage>
                        <Signup />
                      </AnimatedPage>
                    </AuthLayout>
                  </PublicRoute>
                } />
                <Route path="/forgot-password" element={
                  <PublicRoute>
                    <AuthLayout>
                      <AnimatedPage>
                        <ForgotPassword />
                      </AnimatedPage>
                    </AuthLayout>
                  </PublicRoute>
                } />

                {/* Staff Onboarding Route - for invited staff first login */}
                <Route path="/staff-onboarding" element={
                  <ProtectedRoute>
                    <AnimatedPage>
                      <StaffOnboarding />
                    </AnimatedPage>
                  </ProtectedRoute>
                } />

                {/* Profile Completion Route */}
                <Route path="/complete-profile" element={
                  <ProtectedRoute>
                    <AuthLayout>
                      <AnimatedPage>
                        <CompleteProfile />
                      </AnimatedPage>
                    </AuthLayout>
                  </ProtectedRoute>
                } />

                {/* Admin Router */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Outlet />
                    </DashboardLayout>
                  </ProtectedRoute>
                }>
                  <Route index element={<SuperAdminDashboard />} />
                  {/* <Route path="users" element={<UserManagement />} /> */}
                  {/* <Route path="users/:userId" element={<UserDetails />} /> */}
                  {/* <Route path="resources" element={<ResourceMonitor />} /> */}
                  {/* <Route path="security" element={<SecurityCenter />} /> */}
                </Route>
                <Route path="/staff-management" element={
                  <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <StaffManagement />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/import-data" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <DataImport />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                {/* Dashboard Router */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Dashboard />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                {/* Protected Dashboard Routes */}
                <Route path="/sales" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Sales />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/purchases" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Purchases />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/expenses" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant', 'user', 'staff', 'cashier']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Expenses />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoices" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Invoices />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/quotations" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Quotations />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoices/new" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <ModernInvoiceCreation />
                  </ProtectedRoute>
                } />
                <Route path="/quotations/new" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <ModernQuotationCreation />
                  </ProtectedRoute>
                } />
                <Route path="/invoices/new-legacy" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <InvoiceCreation />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoices/edit/:id" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <CreateInvoice />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/clients" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Clients />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/vendors" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Vendors />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/inventory" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant', 'cashier']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Inventory />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/pos" element={
                  <ProtectedRoute allowedRoles={['admin', 'cashier']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <POS />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/payroll" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Payroll />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/leaves" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <LeaveDashboard />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Reports />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/sales-insights" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <SalesInsights />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Settings />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Profile />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/cashbook" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <CashBook />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/bankbook" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <BankBook />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/transactions" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Transactions />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoice-demo" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <InvoiceDemo />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoice-template-preview" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <InvoiceTemplatePreview />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoice-thermal-demo" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <InvoiceThermalDemo />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/invoice-templates/custom" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AnimatedPage>
                        <CustomTemplateBuilder />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/receivables" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <Receivables />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/payables" element={
                  <ProtectedRoute allowedRoles={['admin', 'accountant']}>
                    <DashboardLayout>
                      <AnimatedPage>
                        <AccountsPayable />
                      </AnimatedPage>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />


                {/* Catch all route - smart redirect based on user state */}
                <Route path="*" element={
                  <SmartRedirect />
                } />
              </Routes>
            </AnimatePresence>

            {/* Global monitoring components */}
            {/* <FirebaseConnectionMonitor /> REMOVED */}
            <ToastContainer position="bottom-center" />

            {/* Email Verification Prompt */}
            <EmailVerificationPrompt
              open={showEmailVerification}
              onClose={() => setShowEmailVerification(false)}
              onVerified={handleEmailVerified}
            />

            {/* Show IndexedDB initialization status */}
            {showDBIndicator && (
              <IndexedDBLoadingIndicator onInitialized={handleDBInitialized} />
            )}
          </Router>
        </LocalizationProvider>
      </Box>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <SyncProvider>
            <SetupGuideProvider>
              <WidgetSettingsProvider>
                <ThemeSwitchingApp />
              </WidgetSettingsProvider>
            </SetupGuideProvider>
          </SyncProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App; 